/**
 * SiTLテスト管理ページ
 * make orchestrator方式: モジュール取得 → ビルド → テスト を一貫実行
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { buildApi } from '../services/api';
import { Icon } from '../components/icons';
import { Button, Card, Modal } from '../components/ui';

// テスト実行フェーズ定義
const TEST_PHASES = [
  { key: 'fetch', label: 'モジュール取得', icon: 'download', description: 'Git clone, Docker pull' },
  { key: 'build', label: 'ビルド', icon: 'terminal', description: 'Docker image build' },
  { key: 'test', label: 'テスト実行', icon: 'play', description: 'E2E / Maestro tests' },
];

// フェーズステータス
const PHASE_STATUS = {
  PENDING: { label: '待機中', color: 'bg-slate-200', textColor: 'text-slate-500' },
  RUNNING: { label: '実行中', color: 'bg-blue-500 animate-pulse', textColor: 'text-blue-700' },
  COMPLETED: { label: '完了', color: 'bg-green-500', textColor: 'text-green-700' },
  FAILED: { label: '失敗', color: 'bg-red-500', textColor: 'text-red-700' },
};

// 全体ステータス
const TEST_STATUS = {
  PENDING: { label: '待機中', color: 'bg-slate-100 text-slate-600' },
  QUEUED: { label: 'キュー中', color: 'bg-purple-100 text-purple-700' },
  STARTING_EC2: { label: 'EC2起動中', color: 'bg-yellow-100 text-yellow-700' },
  RUNNING: { label: '実行中', color: 'bg-blue-100 text-blue-700' },
  COMPLETED: { label: '完了', color: 'bg-green-100 text-green-700' },
  FAILED: { label: '失敗', color: 'bg-red-100 text-red-700' },
};

// バイト数フォーマット
const formatBytes = (bytes) => {
  if (bytes === 0 || bytes === null || bytes === undefined) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[Math.min(i, sizes.length - 1)];
};

// testId（YYYYMMDD-HHMMSS-branch）の表示用フォーマット
// ※ テストIDは既にJSTで生成されているため、変換不要
const formatTestIdToJST = (testId) => {
  if (!testId) return '-';
  return testId;  // そのまま返す（既にJST）
};

// 時間軸対応の折れ線グラフコンポーネント
const MetricChart = ({ datapoints, unit, color = '#3b82f6', formatValue, period, tooltip }) => {
  const [showDetail, setShowDetail] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  if (!datapoints || datapoints.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400 text-sm">
        データなし（EC2停止中）
      </div>
    );
  }

  const values = datapoints.map((d) => d.value);
  const maxValue = Math.max(...values, 0.001);
  const minValue = Math.min(...values, 0);
  const avgValue = values.reduce((a, b) => a + b, 0) / values.length;

  // 最大値のデータポイントを見つける
  const maxDatapoint = datapoints.reduce((max, d) => d.value > max.value ? d : max, datapoints[0]);

  // 表示用フォーマッタ
  const displayValue = formatValue || ((v) => {
    if (v === null || v === undefined) return '-';
    if (v >= 1000000000) return (v / 1000000000).toFixed(1) + 'G';
    if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
    if (v >= 1000) return (v / 1000).toFixed(1) + 'K';
    return v.toFixed(1);
  });

  // 時間範囲を計算
  const timestamps = datapoints.map((d) => new Date(d.timestamp).getTime());
  const minTime = Math.min(...timestamps);
  const maxTime = Math.max(...timestamps);
  const timeRange = maxTime - minTime || 1;

  // SVGパスを生成（時間軸ベース）
  const width = 300;
  const height = 120;
  const padding = { top: 10, right: 10, bottom: 25, left: 38 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // ギャップ検出のための閾値（期間の1.5倍以上離れていたらギャップ）
  const gapThreshold = {
    '1h': 3 * 60 * 1000,      // 3分
    '6h': 15 * 60 * 1000,     // 15分
    '24h': 45 * 60 * 1000,    // 45分
    '7d': 3 * 60 * 60 * 1000, // 3時間
    '30d': 3 * 60 * 60 * 1000 // 3時間
  }[period] || 60 * 60 * 1000;

  // セグメント分割（ギャップで区切る）
  const segments = [];
  let currentSegment = [];
  datapoints.forEach((d, i) => {
    if (i === 0) {
      currentSegment.push(d);
    } else {
      const prevTime = new Date(datapoints[i - 1].timestamp).getTime();
      const currTime = new Date(d.timestamp).getTime();
      if (currTime - prevTime > gapThreshold) {
        if (currentSegment.length > 0) segments.push(currentSegment);
        currentSegment = [d];
      } else {
        currentSegment.push(d);
      }
    }
  });
  if (currentSegment.length > 0) segments.push(currentSegment);

  // 座標計算関数
  const getX = (timestamp) => {
    const t = new Date(timestamp).getTime();
    return padding.left + ((t - minTime) / timeRange) * chartWidth;
  };
  const getY = (value) => {
    return padding.top + chartHeight - ((value - minValue) / (maxValue - minValue || 1)) * chartHeight;
  };

  // 時間ラベル生成
  const timeLabels = [];
  const labelCount = 4;
  for (let i = 0; i <= labelCount; i++) {
    const t = new Date(minTime + (timeRange * i / labelCount));
    const label = period === '7d' || period === '30d'
      ? `${t.getMonth() + 1}/${t.getDate()}`
      : `${t.getHours().toString().padStart(2, '0')}:${t.getMinutes().toString().padStart(2, '0')}`;
    timeLabels.push({ x: padding.left + (chartWidth * i / labelCount), label });
  }

  // 値ラベル生成
  const valueLabels = [
    { y: padding.top, value: maxValue },
    { y: padding.top + chartHeight / 2, value: (maxValue + minValue) / 2 },
    { y: padding.top + chartHeight, value: minValue },
  ];

  // 最大値の時刻
  const maxTime_str = new Date(maxDatapoint.timestamp).toLocaleString('ja-JP', {
    month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  return (
    <div className="relative w-full">
      {/* ツールチップアイコン */}
      {tooltip && (
        <div className="absolute top-0 left-0 z-10">
          <button
            className="text-slate-400 hover:text-slate-600 p-1"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={() => setShowTooltip(!showTooltip)}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
          {showTooltip && (
            <div className="absolute left-0 top-6 w-64 p-2 bg-slate-800 text-white text-xs rounded shadow-lg z-20">
              {tooltip}
            </div>
          )}
        </div>
      )}

      {/* グラフ */}
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height: '120px' }}>
        {/* グリッド線 */}
        {valueLabels.map((vl, i) => (
          <line key={i} x1={padding.left} y1={vl.y} x2={width - padding.right} y2={vl.y}
            stroke="#e2e8f0" strokeWidth="1" strokeDasharray="2,2" />
        ))}

        {/* 値軸ラベル */}
        {valueLabels.map((vl, i) => (
          <text key={i} x={padding.left - 3} y={vl.y + 3} fontSize="9" fill="#94a3b8" textAnchor="end">
            {displayValue(vl.value)}
          </text>
        ))}

        {/* セグメントごとに描画（ギャップを空ける） */}
        {segments.map((seg, segIdx) => {
          if (seg.length === 0) return null;
          const points = seg.map((d) => `${getX(d.timestamp)},${getY(d.value)}`).join(' ');
          const areaPath = `M ${getX(seg[0].timestamp)},${padding.top + chartHeight} ` +
            seg.map((d) => `L ${getX(d.timestamp)},${getY(d.value)}`).join(' ') +
            ` L ${getX(seg[seg.length - 1].timestamp)},${padding.top + chartHeight} Z`;
          return (
            <g key={segIdx}>
              <path d={areaPath} fill={color} fillOpacity="0.1" />
              <polyline points={points} fill="none" stroke={color} strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
            </g>
          );
        })}

        {/* 最大値の点（目立つように表示） */}
        <circle cx={getX(maxDatapoint.timestamp)} cy={getY(maxDatapoint.value)}
          r="5" fill="white" stroke={color} strokeWidth="2" />
        <circle cx={getX(maxDatapoint.timestamp)} cy={getY(maxDatapoint.value)}
          r="2" fill={color} />

        {/* 時間軸ラベル */}
        {timeLabels.map((tl, i) => (
          <text key={i} x={tl.x} y={height - 5} fontSize="9" fill="#94a3b8" textAnchor="middle">
            {tl.label}
          </text>
        ))}
      </svg>

      {/* 統計情報 */}
      <div className="flex items-center justify-between mt-1 text-xs">
        <div className="flex gap-3 text-slate-500">
          <span>最小: <span className="font-medium text-slate-700">{displayValue(minValue)}</span></span>
          <span>平均: <span className="font-medium text-slate-700">{displayValue(avgValue)}</span></span>
          <span>最大: <span className="font-medium text-slate-700">{displayValue(maxValue)}</span></span>
        </div>
        <button
          className="text-blue-600 hover:underline"
          onClick={() => setShowDetail(!showDetail)}
        >
          {showDetail ? '閉じる' : '詳細'}
        </button>
      </div>

      {/* 詳細テーブル */}
      {showDetail && (
        <div className="mt-2 max-h-40 overflow-auto border border-slate-200 rounded text-xs">
          <table className="w-full">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-2 py-1 text-left text-slate-600">時刻 (JST)</th>
                <th className="px-2 py-1 text-right text-slate-600">値</th>
              </tr>
            </thead>
            <tbody>
              {[...datapoints].reverse().map((d, i) => (
                <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${d.value === maxValue ? 'bg-yellow-50' : ''}`}>
                  <td className="px-2 py-0.5 text-slate-600">
                    {new Date(d.timestamp).toLocaleString('ja-JP', {
                      month: 'numeric', day: 'numeric',
                      hour: '2-digit', minute: '2-digit'
                    })}
                    {d.value === maxValue && <span className="ml-1 text-yellow-600">★</span>}
                  </td>
                  <td className="px-2 py-0.5 text-right font-mono" style={{ color }}>
                    {displayValue(d.value)} {unit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 期間内最大値表示（コンパクト版） */}
      <div className="absolute top-0 right-0 text-right bg-white/90 px-1 py-0.5 rounded border border-slate-200 text-[10px] leading-tight">
        <div className="text-slate-400">最大</div>
        <span className="text-sm font-semibold" style={{ color }}>
          {displayValue(maxValue)}
        </span>
        <span className="text-slate-500 ml-0.5">{unit}</span>
        <div className="text-slate-400">{maxTime_str}</div>
      </div>
    </div>
  );
};

// テスト詳細コンテンツコンポーネント
const TestDetailContent = ({
  testDetail,
  screenshots,
  coverageReport,
  formatDate,
  calcDuration,
  handleWatchVideo,
  handleViewScreenshot,
  handleDownloadLog,
}) => (
  <div className="space-y-6">
    {/* ヘッダー */}
    <div className="flex items-center justify-between">
      <h3 className="font-bold text-lg text-slate-900">{formatTestIdToJST(testDetail.testId)}</h3>
      {testDetail.status !== 'RUNNING' && testDetail.status !== 'STARTING_EC2' && handleDownloadLog && (
        <div className="relative group">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleDownloadLog(testDetail.testId)}
          >
            <Icon name="download" className="w-3 h-3" />
            ログをダウンロード
          </Button>
          <div className="absolute right-0 top-full mt-2 w-72 p-2 bg-slate-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
            コンテナログは末尾のみ記録（Ametori/Karas E2E: 2000行、XRCE Agent: 50行、Logcat: 5000行）。設定はuav-dev-env/makefileで変更可能。
          </div>
        </div>
      )}
    </div>

    {/* サマリー情報 */}
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-slate-50 rounded-lg">
      <div>
        <div className="text-xs text-slate-500 mb-1">開始日時</div>
        <div className="font-medium text-slate-900">
          {testDetail.startedAt ? formatDate(testDetail.startedAt) : '-'}
        </div>
      </div>
      <div>
        <div className="text-xs text-slate-500 mb-1">完了日時</div>
        <div className="font-medium text-slate-900">
          {testDetail.completedAt ? formatDate(testDetail.completedAt) : '-'}
        </div>
      </div>
      <div>
        <div className="text-xs text-slate-500 mb-1">所要時間</div>
        <div className="font-medium text-slate-900">
          {testDetail.completedAt ? calcDuration(testDetail.startedAt, testDetail.completedAt) :
           testDetail.status === 'RUNNING' ? calcDuration(testDetail.startedAt) : '-'}
        </div>
      </div>
      <div>
        <div className="text-xs text-slate-500 mb-1">終了コード</div>
        <div className="font-medium">
          {testDetail.exitCode !== undefined ? (
            <span className={testDetail.exitCode === 0 ? 'text-green-600' : 'text-red-600'}>
              {testDetail.exitCode}
            </span>
          ) : '-'}
        </div>
      </div>
      <div>
        <div className="text-xs text-slate-500 mb-1">Dockerクリア</div>
        <div className="font-medium">
          {testDetail.cleanDocker ? (
            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-sm">
              🧹 実行
            </span>
          ) : (
            <span className="text-slate-400">-</span>
          )}
        </div>
      </div>
      <div>
        <div className="text-xs text-slate-500 mb-1">Gitクリーン</div>
        <div className="font-medium">
          {testDetail.gitClean ? (
            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-sm">
              🔄 clone
            </span>
          ) : (
            <span className="text-slate-400">fetch</span>
          )}
        </div>
      </div>
    </div>

    {/* Git情報 */}
    {testDetail.commitId && (
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
          <Icon name="git-branch" className="w-4 h-4" />
          Git情報
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-slate-500">ブランチ:</span>{' '}
            <span className="font-mono font-medium text-slate-900">{testDetail.branch}</span>
          </div>
          <div>
            <span className="text-slate-500">コミット:</span>{' '}
            <span className="font-mono font-medium text-slate-900">{testDetail.commitShort || testDetail.commitId?.slice(0, 7)}</span>
          </div>
          {testDetail.commitMessage && (
            <div className="md:col-span-2">
              <span className="text-slate-500">メッセージ:</span>{' '}
              <span className="text-slate-900">{testDetail.commitMessage}</span>
            </div>
          )}
          {testDetail.commitDate && (
            <div>
              <span className="text-slate-500">コミット日時:</span>{' '}
              <span className="text-slate-900">{testDetail.commitDate}</span>
            </div>
          )}
        </div>
      </div>
    )}

    {/* ファイル一覧 */}
    <div>
      {(() => {
        const displayFiles = testDetail.files?.filter(
          (f) => !['status.json'].includes(f.name)
        ) || [];
        return (
          <>
            <h4 className="font-medium text-slate-700 mb-2">ファイル ({displayFiles.length})</h4>
            <div className="space-y-1">
              {displayFiles.map((file) => (
                <div key={file.key} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <div className="flex items-center gap-2">
                    <Icon
                      name={file.type === 'video' ? 'play' : 'file'}
                      className="w-4 h-4 text-slate-400"
                    />
                    <span className="text-sm">{file.name}</span>
                    <span className="text-xs text-slate-400">
                      ({file.size ? `${(file.size / 1024 / 1024).toFixed(1)}MB` : '-'})
                    </span>
                  </div>
                  {file.type === 'video' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleWatchVideo(testDetail.testId, file.name)}
                    >
                      <Icon name="play" className="w-3 h-3" /> 再生
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </>
        );
      })()}
    </div>

    {/* スクリーンショット */}
    {screenshots.length > 0 && (
      <div>
        <h4 className="font-medium text-slate-700 mb-2">スクリーンショット ({screenshots.length})</h4>
        <div className="grid grid-cols-4 gap-2">
          {screenshots.map((ss) => (
            <div
              key={ss.key}
              className="relative cursor-pointer group"
              onClick={() => handleViewScreenshot(testDetail.testId, ss.name)}
            >
              <div className="aspect-video bg-slate-100 rounded border border-slate-200 flex items-center justify-center">
                <Icon name="image" className="w-6 h-6 text-slate-300" />
              </div>
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
                <Icon name="zoom-in" className="w-5 h-5 text-white" />
              </div>
              <div className="text-xs text-slate-500 truncate mt-1">{ss.name}</div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* カバレッジレポート */}
    {coverageReport?.requirements && (
      <div>
        <h4 className="font-medium text-slate-700 mb-2">
          要求カバレッジ ({coverageReport.summary?.pass || 0} PASS / {coverageReport.summary?.fail || 0} FAIL)
        </h4>
        <div className="overflow-x-auto max-h-64 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
          <table className="w-full text-xs">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="px-2 py-1 text-left">ID</th>
                <th className="px-2 py-1 text-left">結果</th>
                <th className="px-2 py-1 text-left">Name</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {coverageReport.requirements.map((req) => (
                <tr key={req.id} className="hover:bg-slate-50">
                  <td className="px-2 py-1 font-mono">{req.id}</td>
                  <td className="px-2 py-1">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      req.result === 'PASS' ? 'bg-green-100 text-green-700' :
                      req.result === 'FAIL' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-500'
                    }`}>
                      {req.result || 'NO_TEST'}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-slate-600">{req.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

// System Requirement Coverageコンテンツコンポーネント
const ScenarioCoverageContent = ({ scenarioResults, setScenarioResults }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <div>
        <h3 className="font-bold text-slate-900">System Requirement Coverage</h3>
        {scenarioResults.results && (
          <div className="flex items-center gap-4 mt-1 text-sm">
            <span className="text-green-600 font-medium">
              OK: {scenarioResults.results.filter(r => r.verdict === 'OK' || r.verdict === 'PROMOTED').length}
            </span>
            <span className="text-red-600 font-medium">
              REGRESSION: {scenarioResults.results.filter(r => r.verdict === 'REGRESSION').length}
            </span>
            <span className="text-slate-500">
              NO_TEST: {scenarioResults.results.filter(r => r.sitl === 'NO_TEST' || r.sitl === '-' || !r.sitl).length}
            </span>
          </div>
        )}
        {/* REGRESSIONサマリー */}
        {(() => {
          const regressions = scenarioResults.results.filter(r => r.verdict === 'REGRESSION');
          if (regressions.length === 0) return null;
          return (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700 text-sm font-medium mb-1">
                <Icon name="alert-triangle" className="w-4 h-4" />
                REGRESSION ({regressions.length}件)
              </div>
              <div className="flex flex-wrap gap-1">
                {regressions.map((r) => (
                  <span
                    key={r.id}
                    className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-mono rounded cursor-help"
                    title={`${r.name}\nSITL: ${r.sitl}, Expected: ${r.expected}\n${r.acceptance || ''}`}
                  >
                    {r.id}
                  </span>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => setScenarioResults(null)}
      >
        <Icon name="x" className="w-4 h-4" /> 閉じる
      </Button>
    </div>

    <div className="overflow-x-auto max-h-96 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
      <table className="w-full text-xs">
        <thead className="bg-slate-100 sticky top-0">
          <tr>
            <th className="px-2 py-2 text-left font-medium text-slate-600 whitespace-nowrap">ID</th>
            <th className="px-2 py-2 text-center font-medium text-slate-600">SITL</th>
            <th className="px-2 py-2 text-center font-medium text-slate-600">Expected</th>
            <th className="px-2 py-2 text-center font-medium text-slate-600">Verdict</th>
            <th className="px-2 py-2 text-left font-medium text-slate-600">Name</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {scenarioResults.results.map((row, idx) => (
            <tr key={idx} className="hover:bg-slate-50">
              <td className="px-2 py-1.5 font-mono text-slate-900 whitespace-nowrap">{row.id}</td>
              <td className="px-2 py-1.5 text-center">
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                  row.sitl === 'PASS' ? 'bg-green-100 text-green-700' :
                  row.sitl === 'FAIL' ? 'bg-red-100 text-red-700' :
                  row.sitl === 'NO_TEST' ? 'bg-slate-100 text-slate-500' :
                  'bg-slate-50 text-slate-400'
                }`}>
                  {row.sitl}
                </span>
              </td>
              <td className="px-2 py-1.5 text-center">
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  row.expected === 'PASS' ? 'bg-green-50 text-green-600' :
                  row.expected === 'FAIL' ? 'bg-red-50 text-red-600' :
                  'text-slate-400'
                }`}>
                  {row.expected}
                </span>
              </td>
              <td className="px-2 py-1.5 text-center">
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                  row.verdict === 'OK' ? 'bg-green-100 text-green-700' :
                  row.verdict === 'PROMOTED' ? 'bg-green-100 text-green-700' :
                  row.verdict === 'REGRESSION' ? 'bg-red-100 text-red-700' :
                  'text-slate-400'
                }`}>
                  {row.verdict || '-'}
                </span>
              </td>
              <td className="px-2 py-1.5 text-slate-700">{row.name}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default function SiTLTests() {
  // URLクエリパラメータ
  const [searchParams, setSearchParams] = useSearchParams();
  const queryTestId = searchParams.get('testId');

  // テスト実行一覧
  const [testRuns, setTestRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);

  // 選択中のテスト
  const [selectedTest, setSelectedTest] = useState(null);
  const [testDetail, setTestDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // リアルタイム監視
  const [monitoringTestId, setMonitoringTestId] = useState(null);
  const [testStatus, setTestStatus] = useState(null);
  const [streamingLogs, setStreamingLogs] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [apiPhases, setApiPhases] = useState(null);  // APIから取得したフェーズ情報
  const logRef = useRef(null);

  // モーダル
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);

  // スクリーンショット・カバレッジ
  const [screenshots, setScreenshots] = useState([]);
  const [coverageReport, setCoverageReport] = useState(null);

  // テストシナリオ結果 (System Requirement Coverage)
  const [scenarioResults, setScenarioResults] = useState(null);

  // フィルター
  const [filter, setFilter] = useState({ status: '', dateFrom: '', dateTo: '' });

  // テスト実行中フラグ
  const [isStartingTest, setIsStartingTest] = useState(false);
  const [isStoppingTest, setIsStoppingTest] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [runningTestId, setRunningTestId] = useState(null);  // 現在実行中のテストID
  const [cleanDocker, setCleanDocker] = useState(false);  // Dockerクリーンアップオプション

  // デバウンス用Ref（連続呼び出し防止）
  const lastFetchTimeRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  const [branch, setBranch] = useState('main');  // ブランチ指定
  const [branches, setBranches] = useState(['main']);  // 利用可能なブランチ一覧
  const [branchesLoading, setBranchesLoading] = useState(false);  // ブランチ取得中
  const [gitClean, setGitClean] = useState(false);  // Gitクリーンビルドオプション

  // タイマー設定
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [scheduledTime, setScheduledTime] = useState('');
  const [dailyMode, setDailyMode] = useState(false);
  const [dailyTime, setDailyTime] = useState('22:30');
  const [scheduledTests, setScheduledTests] = useState([]);

  // EC2監視
  const [ec2Status, setEc2Status] = useState(null);
  const [ec2Loading, setEc2Loading] = useState(true);
  const [ec2ActionLoading, setEc2ActionLoading] = useState(false);
  const [ec2Lifecycle, setEc2Lifecycle] = useState(null);

  // EC2詳細パネル
  const [showEc2Details, setShowEc2Details] = useState(false);
  const [ec2Metrics, setEc2Metrics] = useState(null);
  const [ec2MetricsLoading, setEc2MetricsLoading] = useState(false);
  const [ec2MetricsPeriod, setEc2MetricsPeriod] = useState('6h');
  const ec2DetailsRef = useRef(null);

  // テスト一覧取得（レート制限対策：デバウンス＋リトライ）
  const fetchTestRuns = useCallback(async (retryCount = 0) => {
    // 5秒以内の連続呼び出しを防止
    const now = Date.now();
    if (now - lastFetchTimeRef.current < 5000 && !isInitialLoadRef.current) {
      console.log('fetchTestRuns skipped (debounce)');
      return;
    }
    lastFetchTimeRef.current = now;

    // 初回ロード時のみローディング表示（チラつき防止）
    const isInitial = isInitialLoadRef.current;
    if (isInitial) {
      setLoading(true);
    }

    try {
      const result = await buildApi.listTestRuns({ limit: 10, offset: 0 });
      setTestRuns(result.testRuns || []);
      setHasMore(result.hasMore || false);
      setTotalCount(result.total || 0);
      setError(null);
      isInitialLoadRef.current = false;
    } catch (err) {
      console.error('Failed to fetch test runs:', err);
      // レート制限エラーの場合はリトライ
      if (err.message?.includes('reduce your request rate') && retryCount < 2) {
        console.log(`Retrying fetchTestRuns (${retryCount + 1}/2)...`);
        await new Promise(resolve => setTimeout(resolve, 3000));
        return fetchTestRuns(retryCount + 1);
      }
      setError(err.message);
    } finally {
      if (isInitial) {
        setLoading(false);
      }
    }
  }, []);

  // もっと読み込む
  const loadMoreTests = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const result = await buildApi.listTestRuns({ limit: 10, offset: testRuns.length });
      setTestRuns((prev) => [...prev, ...(result.testRuns || [])]);
      setHasMore(result.hasMore || false);
      setTotalCount(result.total || 0);
    } catch (err) {
      console.error('Failed to load more test runs:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, testRuns.length]);

  // テスト詳細取得
  const fetchTestDetail = useCallback(async (testId) => {
    setDetailLoading(true);
    try {
      const result = await buildApi.getTestRun(testId);
      setTestDetail(result);
    } catch (err) {
      console.error('Failed to fetch test detail:', err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // テストステータス取得
  const fetchTestStatus = useCallback(async (testId) => {
    try {
      const result = await buildApi.getTestRunStatus(testId);
      setTestStatus(result);
    } catch (err) {
      console.error('Failed to fetch test status:', err);
    }
  }, []);

  // ログストリーミング取得
  const fetchLogStream = useCallback(async (testId) => {
    try {
      const result = await buildApi.getTestRunLogStream(testId, { lines: 1000 });
      if (result.logs) {
        setStreamingLogs(result.logs);
        // フェーズ情報があれば保存
        if (result.phases) {
          setApiPhases(result.phases);
        }
      } else if (result.debug) {
        // デバッグ情報がある場合は表示
        setStreamingLogs(`ログ取得中...\n\n${result.debug.join('\n')}`);
      } else {
        setStreamingLogs('ログを取得中...');
      }
    } catch (err) {
      console.error('Failed to fetch log stream:', err);
      setStreamingLogs(`エラー: ${err.message}`);
    }
  }, []);

  // スクリーンショット取得
  const fetchScreenshots = useCallback(async (testId) => {
    try {
      const result = await buildApi.listScreenshots(testId);
      setScreenshots(result.screenshots || []);
    } catch (err) {
      console.error('Failed to fetch screenshots:', err);
      setScreenshots([]);
    }
  }, []);

  // カバレッジ取得
  const fetchCoverageReport = useCallback(async (testId) => {
    try {
      const result = await buildApi.getCoverageReport(testId);
      setCoverageReport(result.report);
    } catch (err) {
      console.error('Failed to fetch coverage:', err);
      setCoverageReport(null);
    }
  }, []);

  // 監視開始
  const startMonitoring = useCallback((testId) => {
    setMonitoringTestId(testId);
    setAutoRefresh(true);
    setStreamingLogs('');
    setTestStatus(null);
    setApiPhases(null);
    setScenarioResults(null);  // 前のテスト結果をクリア
    fetchTestStatus(testId);
    fetchLogStream(testId);
  }, [fetchTestStatus, fetchLogStream]);

  // 監視停止
  const stopMonitoring = useCallback(() => {
    setMonitoringTestId(null);
    setAutoRefresh(false);
  }, []);

  // EC2ステータス取得
  const fetchEC2Status = useCallback(async () => {
    try {
      const result = await buildApi.getEC2Status();
      setEc2Status(result);
    } catch (err) {
      console.error('Failed to fetch EC2 status:', err);
      setEc2Status({ error: err.message, status: 'error' });
    } finally {
      setEc2Loading(false);
    }
  }, []);

  // EC2ライフサイクル（タイマー情報）取得
  const fetchEC2Lifecycle = useCallback(async () => {
    try {
      const result = await buildApi.getEC2Lifecycle();
      setEc2Lifecycle(result);
    } catch (err) {
      console.error('Failed to fetch EC2 lifecycle:', err);
    }
  }, []);

  // EC2メトリクス取得（periodは呼び出し時に明示的に渡す）
  const fetchEC2Metrics = useCallback(async (period) => {
    setEc2MetricsLoading(true);
    try {
      const result = await buildApi.getEC2Metrics(period);
      setEc2Metrics(result);
    } catch (err) {
      console.error('Failed to fetch EC2 metrics:', err);
    } finally {
      setEc2MetricsLoading(false);
    }
  }, []);

  // Gitブランチ一覧取得
  const fetchBranches = useCallback(async () => {
    setBranchesLoading(true);
    try {
      const result = await buildApi.getGitBranches();
      if (result.branches && result.branches.length > 0) {
        setBranches(result.branches);
        // 現在選択中のブランチが一覧にない場合はmainに戻す
        setBranch(currentBranch => {
          if (!result.branches.includes(currentBranch)) {
            return result.branches[0];
          }
          return currentBranch;
        });
      }
    } catch (err) {
      console.error('Failed to fetch branches:', err);
    } finally {
      setBranchesLoading(false);
    }
  }, []);

  // EC2起動
  const handleStartEC2 = async () => {
    if (!window.confirm('EC2インスタンスを起動しますか？')) return;
    setEc2ActionLoading(true);
    try {
      await buildApi.startEC2({ reason: 'manual' });
      // ステータスを更新（起動完了を待つため複数回リトライ）
      for (let i = 0; i < 6; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        await fetchEC2Status();
        if (ec2Status?.state === 'running') break;
      }
    } catch (err) {
      alert('EC2起動に失敗しました: ' + err.message);
    } finally {
      setEc2ActionLoading(false);
    }
  };

  // EC2停止
  const handleStopEC2 = async () => {
    if (!window.confirm('EC2インスタンスを停止しますか？\n\nテストが実行中の場合は停止できません。')) return;
    setEc2ActionLoading(true);
    try {
      await buildApi.stopEC2({ reason: 'manual' });
      await fetchEC2Status();
    } catch (err) {
      alert('EC2停止に失敗しました: ' + err.message);
    } finally {
      setEc2ActionLoading(false);
    }
  };

  // スケジュールテスト一覧取得
  const fetchScheduledTests = useCallback(async () => {
    try {
      const result = await buildApi.listScheduledTests();
      setScheduledTests(result.schedules || []);
    } catch (err) {
      console.error('Failed to fetch scheduled tests:', err);
    }
  }, []);

  // スケジュールテスト設定
  const handleScheduleTest = async () => {
    if (dailyMode) {
      if (!dailyTime) {
        alert('実行時刻を設定してください');
        return;
      }
    } else {
      if (!scheduledTime) {
        alert('実行日時を設定してください');
        return;
      }
    }
    try {
      await buildApi.scheduleTest({
        daily: dailyMode,
        dailyTime: dailyMode ? dailyTime : undefined,
        scheduledTime: dailyMode ? undefined : scheduledTime,
        cleanDocker,
        gitClean,
        repository: 'uav-dev-env',
        branch,
      });
      // ダイアログは閉じずに一覧を更新（登録結果を確認できるように）
      setScheduledTime('');
      fetchScheduledTests();
    } catch (err) {
      console.error('Failed to schedule test:', err);
      alert(`スケジュール設定に失敗しました: ${err.message}`);
    }
  };

  // スケジュールテスト削除
  const handleCancelSchedule = async (scheduleId) => {
    if (!window.confirm('このスケジュールをキャンセルしますか？')) return;
    try {
      await buildApi.cancelScheduledTest(scheduleId);
      fetchScheduledTests();
    } catch (err) {
      console.error('Failed to cancel schedule:', err);
      alert(`スケジュールのキャンセルに失敗しました: ${err.message}`);
    }
  };

  // テスト実行開始（確認モーダル表示）
  const handleStartTestClick = () => {
    if (isStartingTest || runningTestId) return;
    setShowConfirmModal(true);
    fetchBranches();  // ブランチ一覧を取得
  };

  // テスト実行開始（実際の処理）
  const handleStartTest = async () => {
    setShowConfirmModal(false);
    if (isStartingTest) return;

    setIsStartingTest(true);
    try {
      const result = await buildApi.startTestRun({
        repository: 'uav-dev-env',
        branch,
        testType: 'sitl',
        cleanDocker,
        gitClean,
      });

      // EC2起動中の場合はポーリングして起動完了後に再実行
      if (result.status === 'STARTING_EC2' || result.ec2Starting) {
        // 新しいテストを履歴に追加（STARTING_EC2状態で）
        const newTest = {
          testId: result.testId,
          status: 'STARTING_EC2',
          createdAt: new Date().toISOString(),
        };
        setTestRuns((prev) => [newTest, ...prev]);
        setSelectedTest(newTest);

        // 監視開始（STARTING_EC2状態用のメッセージを設定）
        setMonitoringTestId(result.testId);
        setAutoRefresh(true);
        setStreamingLogs('EC2を起動中です...\n\n起動完了後、自動的にテストが開始されます。');
        setTestStatus({ status: 'STARTING_EC2', phase: 'EC2_STARTING' });

        // EC2起動を待って自動でテストを再実行
        let retryCount = 0;
        let ec2StartRequested = false;
        const maxRetries = 40; // 最大約7分（10秒 x 40回）
        const pollInterval = setInterval(async () => {
          retryCount++;
          try {
            const ec2Result = await buildApi.getEC2Status();
            console.log(`EC2 poll #${retryCount}: state=${ec2Result.state}`);

            // stopping状態の場合は待機
            if (ec2Result.state === 'stopping') {
              setStreamingLogs(`EC2停止処理中...\n\n停止完了後、EC2を起動してテストを開始します。\n\n[${retryCount}/${maxRetries}] 状態: stopping`);
              return;
            }

            // stopped状態になったらEC2を起動
            if (ec2Result.state === 'stopped' && !ec2StartRequested) {
              console.log('EC2 stopped, starting...');
              ec2StartRequested = true;
              setStreamingLogs(`EC2を起動しています...\n\n[${retryCount}/${maxRetries}] 状態: stopped → 起動中`);
              await buildApi.startEC2({ reason: 'test-auto' });
              return;
            }

            // pending状態の場合は待機
            if (ec2Result.state === 'pending') {
              setStreamingLogs(`EC2起動中...\n\nSSMエージェントの起動を待っています。\n\n[${retryCount}/${maxRetries}] 状態: pending`);
              return;
            }

            // runningになったらテストを再実行
            if (ec2Result.state === 'running') {
              clearInterval(pollInterval);
              setStreamingLogs(`EC2起動完了\n\nSSMエージェントの準備を待っています（20秒）...`);
              // 起動完了、SSMエージェント待機のため追加で20秒待つ
              await new Promise(r => setTimeout(r, 20000));
              setStreamingLogs(`テストを開始しています...`);
              // テストを再実行
              const retryResult = await buildApi.startTestRun({
                testId: result.testId,
                repository: 'uav-dev-env',
                branch,
                testType: 'sitl',
                cleanDocker,
                gitClean,
              });
              if (retryResult.status === 'RUNNING') {
                setRunningTestId(retryResult.testId);
                setTestRuns((prev) => prev.map(t =>
                  t.testId === result.testId ? { ...t, status: 'RUNNING' } : t
                ));
                startMonitoring(retryResult.testId);
              }
              setIsStartingTest(false);
            } else if (retryCount >= maxRetries) {
              clearInterval(pollInterval);
              alert('EC2の起動がタイムアウトしました。手動で再実行してください。');
              setIsStartingTest(false);
            }
          } catch (pollErr) {
            console.error('EC2 status poll error:', pollErr);
          }
        }, 10000);

        return; // isStartingTestはポーリング完了後にfalseにする
      }

      if (result.testId) {
        // 実行中のテストIDを保存
        setRunningTestId(result.testId);

        // 新しいテストを履歴リストの先頭に追加
        const newTest = {
          testId: result.testId,
          status: 'RUNNING',
          createdAt: new Date().toISOString(),
        };
        setTestRuns((prev) => [newTest, ...prev]);

        // 新しいテストを選択
        setSelectedTest(newTest);

        // 監視開始
        startMonitoring(result.testId);
      }
    } catch (err) {
      console.error('Failed to start test:', err);
      alert(`テスト実行に失敗しました: ${err.message}`);
    } finally {
      setIsStartingTest(false);
    }
  };

  // テスト中止
  const handleStopTest = async () => {
    if (!runningTestId || isStoppingTest) return;

    if (!window.confirm('実行中のテストを中止しますか？')) return;

    setIsStoppingTest(true);
    try {
      await buildApi.stopTestRun(runningTestId);
      setRunningTestId(null);
      stopMonitoring();
      // 少し待ってからリスト更新
      setTimeout(fetchTestRuns, 2000);
    } catch (err) {
      console.error('Failed to stop test:', err);
      alert(`テスト中止に失敗しました: ${err.message}`);
    } finally {
      setIsStoppingTest(false);
    }
  };

  // 動画再生
  const handleWatchVideo = async (testId, fileName = 'simulation.mp4') => {
    try {
      const result = await buildApi.getTestVideoUrl(testId, fileName);
      setVideoUrl(result.presignedUrl);
      setShowVideoModal(true);
    } catch (err) {
      alert('動画URLの取得に失敗しました: ' + err.message);
    }
  };

  // ログダウンロード
  const handleDownloadLog = async (testId) => {
    try {
      const result = await buildApi.getTestLogUrl(testId);
      if (result.presignedUrl) {
        // testIdをJST形式に変換（例: 20260526-165547-main.log）
        const fileName = `${formatTestIdToJST(testId)}.log`;
        // 新しいタブでダウンロード
        const link = document.createElement('a');
        link.href = result.presignedUrl;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      alert('ログのダウンロードに失敗しました: ' + err.message);
    }
  };

  // スクリーンショット表示
  const handleViewScreenshot = async (testId, fileName) => {
    try {
      const result = await buildApi.getScreenshotUrl(testId, fileName);
      setScreenshotUrl(result.presignedUrl);
      setSelectedScreenshot(fileName);
      setShowScreenshotModal(true);
    } catch (err) {
      alert('スクリーンショットの取得に失敗しました: ' + err.message);
    }
  };

  // 初期ロード（依存配列を空にして1回だけ実行）
  // eslint-disable-next-line
  useEffect(() => {
    fetchTestRuns();
    fetchEC2Status();
    fetchEC2Lifecycle();
    fetchScheduledTests();
  }, []);

  // RUNNINGテストを検出してrunningTestIdを設定（ページリロード対応）
  // また、selectedTestがない場合は最新のテストを自動選択
  // クエリパラメータでtestIdが指定されている場合はそれを優先
  useEffect(() => {
    if (testRuns.length > 0) {
      // RUNNINGテストを検出
      if (!runningTestId) {
        const runningTest = testRuns.find(t => t.status === 'RUNNING' || t.status === 'STARTING_EC2');
        // 最近完了したテストは無視（終了直後の再設定防止）
        if (runningTest && completedTestRef.current !== runningTest.testId) {
          setRunningTestId(runningTest.testId);
          // 自動的に監視開始
          setMonitoringTestId(runningTest.testId);
          setAutoRefresh(true);
        }
      }

      // テスト選択ロジック
      if (!selectedTest) {
        // クエリパラメータでtestIdが指定されている場合はそれを選択
        if (queryTestId) {
          const targetTest = testRuns.find(t => t.testId === queryTestId);
          if (targetTest) {
            setSelectedTest(targetTest);
            // クエリパラメータをクリア（URLをきれいにする）
            setSearchParams({}, { replace: true });
            return;
          }
        }
        // 指定がない場合は最新のテストを自動選択
        setSelectedTest(testRuns[0]);
      }
    }
  }, [testRuns, runningTestId, selectedTest, queryTestId, setSearchParams]);

  // EC2ステータス・ライフサイクル定期更新（30秒間隔）
  useEffect(() => {
    const interval = setInterval(() => {
      fetchEC2Status();
      fetchEC2Lifecycle();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchEC2Status, fetchEC2Lifecycle]);

  // テスト選択時
  useEffect(() => {
    if (selectedTest) {
      fetchTestDetail(selectedTest.testId);
      fetchScreenshots(selectedTest.testId);
      fetchCoverageReport(selectedTest.testId);
      // 前のテストのシナリオ結果をクリア
      setScenarioResults(null);
    } else {
      setTestDetail(null);
      setScreenshots([]);
      setCoverageReport(null);
      setScenarioResults(null);
    }
  }, [selectedTest, fetchTestDetail, fetchScreenshots, fetchCoverageReport]);

  // リアルタイム更新
  useEffect(() => {
    if (!monitoringTestId || !autoRefresh) return;

    const interval = setInterval(() => {
      fetchTestStatus(monitoringTestId);
      fetchLogStream(monitoringTestId);
    }, 10000); // 10秒間隔（パフォーマンス改善）

    return () => clearInterval(interval);
  }, [monitoringTestId, autoRefresh, fetchTestStatus, fetchLogStream]);

  // テスト完了検出用のRef（重複実行防止）
  const completedTestRef = useRef(null);

  // テスト完了検出（runningTestIdをクリア、autoRefresh停止、EC2自動停止）
  useEffect(() => {
    if (!testStatus || !monitoringTestId) return;

    const status = testStatus.status || '';
    const isCompleted = status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELLED';

    // 既に処理済みのテストは無視（重複実行防止）
    if (isCompleted && completedTestRef.current === monitoringTestId) {
      return;
    }

    if (isCompleted) {
      // 処理済みとしてマーク
      completedTestRef.current = monitoringTestId;

      // 実行中のテストIDをクリア
      if (runningTestId) {
        setRunningTestId(null);
      }

      // テスト完了後にEC2を自動停止（テスト起動の場合のみ）
      const shouldAutoStop =
        ec2Lifecycle?.ec2StartedBy === 'test' &&
        ec2Status?.state === 'running' &&
        (runningTestId || monitoringTestId === ec2Lifecycle?.currentTestId);

      if (shouldAutoStop) {
        console.log('Test completed (test-started EC2), auto-stopping EC2...');
        buildApi.stopEC2({ reason: 'test-completed' }).then(() => {
          fetchEC2Status();
          fetchEC2Lifecycle();
        }).catch(err => {
          console.error('Failed to auto-stop EC2:', err);
        });
      }

      // 自動更新を停止
      if (autoRefresh) {
        setAutoRefresh(false);
      }
      // リスト更新（レート制限回避のため遅延）
      setTimeout(() => fetchTestRuns(), 3000);
    }
  }, [testStatus, runningTestId, monitoringTestId, autoRefresh, fetchTestRuns, ec2Lifecycle, ec2Status, fetchEC2Status, fetchEC2Lifecycle]);

  // ログ自動スクロール
  useEffect(() => {
    if (autoRefresh && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [streamingLogs, autoRefresh]);

  // フェーズを推測（ログ内容も考慮）
  const inferPhase = useCallback((status) => {
    if (!status) return { fetch: 'PENDING', build: 'PENDING', test: 'PENDING' };

    const phase = status.phase || '';
    const overallStatus = status.status || 'UNKNOWN';

    if (overallStatus === 'COMPLETED') {
      return { fetch: 'COMPLETED', build: 'COMPLETED', test: 'COMPLETED' };
    }

    if (overallStatus === 'FAILED' || overallStatus === 'CANCELLED') {
      // ログ内容からどのフェーズで失敗したか推測
      if (streamingLogs) {
        // テストフェーズで失敗したかどうかを確認
        if (streamingLogs.includes('System Requirement Coverage') ||
            streamingLogs.includes('=== Karas Logcat ===') ||
            streamingLogs.includes('Starting E2E') ||
            streamingLogs.includes('Going to remove')) {
          return { fetch: 'COMPLETED', build: 'COMPLETED', test: 'FAILED' };
        }
        // ビルドフェーズで失敗したかどうかを確認
        if (streamingLogs.includes('Building') ||
            streamingLogs.includes('docker compose') ||
            streamingLogs.includes('Container') ||
            (streamingLogs.includes('make ') && !streamingLogs.includes('git clone'))) {
          return { fetch: 'COMPLETED', build: 'FAILED', test: 'PENDING' };
        }
      }
      // phaseがある場合はそれを使用
      if (phase.includes('BUILD') || phase.includes('BUILDING')) {
        return { fetch: 'COMPLETED', build: 'FAILED', test: 'PENDING' };
      }
      if (phase.includes('TEST') || phase.includes('E2E') || phase.includes('EMULATOR') || phase.includes('SIMULATION')) {
        return { fetch: 'COMPLETED', build: 'COMPLETED', test: 'FAILED' };
      }
      // デフォルト：fetchで失敗
      return { fetch: 'FAILED', build: 'PENDING', test: 'PENDING' };
    }

    // RUNNING状態
    if (phase.includes('BUILD') || phase.includes('BUILDING')) {
      return { fetch: 'COMPLETED', build: 'RUNNING', test: 'PENDING' };
    }
    if (phase.includes('TEST') || phase.includes('E2E') || phase.includes('EMULATOR')) {
      return { fetch: 'COMPLETED', build: 'COMPLETED', test: 'RUNNING' };
    }
    if (phase.includes('SIMULATION')) {
      return { fetch: 'COMPLETED', build: 'COMPLETED', test: 'RUNNING' };
    }

    return { fetch: 'RUNNING', build: 'PENDING', test: 'PENDING' };
  }, [streamingLogs]);

  // フィルター適用
  const filteredTestRuns = testRuns.filter((run) => {
    if (filter.status && run.status !== filter.status) return false;
    if (filter.dateFrom && run.createdAt) {
      if (new Date(run.createdAt) < new Date(filter.dateFrom)) return false;
    }
    if (filter.dateTo && run.createdAt) {
      const toDate = new Date(filter.dateTo);
      toDate.setHours(23, 59, 59);
      if (new Date(run.createdAt) > toDate) return false;
    }
    return true;
  });

  // 日付フォーマット
  const formatDate = (isoStr) => {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleString('ja-JP', {
      month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  };

  // ログ内のUTC日時をJSTに変換
  const convertLogTimestamps = (logContent) => {
    if (!logContent) return logContent;
    // ISO 8601形式のタイムスタンプを検出 (例: 2026-05-21T00:02:12Z, 2026-05-21T00:02:12.123Z)
    return logContent.replace(
      /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z?)/g,
      (match) => {
        try {
          const date = new Date(match.endsWith('Z') ? match : match + 'Z');
          return date.toLocaleString('ja-JP', {
            month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
          }) + ' JST';
        } catch {
          return match;
        }
      }
    );
  };

  // 所要時間計算
  const calcDuration = (start, end) => {
    if (!start) return '-';
    const diffMs = (end ? new Date(end) : new Date()) - new Date(start);
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 60) return `${diffMin}分`;
    return `${Math.floor(diffMin / 60)}時間${diffMin % 60}分`;
  };

  // テストシナリオ結果をログからパース
  const parseScenarioResults = useCallback((logContent) => {
    if (!logContent) return null;

    // System Requirement Coverage セクションを探す
    const coverageStart = logContent.indexOf('System Requirement Coverage');
    if (coverageStart === -1) return null;

    const lines = logContent.substring(coverageStart).split('\n');
    const results = [];
    let summary = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // サマリー行を検出
      if (line.match(/SITL:\s*\d+\s*PASS/)) {
        const match = line.match(/SITL:\s*(\d+)\s*PASS,\s*(\d+)\s*FAIL,\s*(\d+)\s*NO_TEST/);
        if (match) {
          summary = {
            pass: parseInt(match[1], 10),
            fail: parseInt(match[2], 10),
            noTest: parseInt(match[3], 10),
          };
        }
        break;
      }

      // 結果行を検出（SRV- で始まる行）
      if (line.startsWith('SRV-')) {
        // フォーマット: ID  SITL  Expected  Verdict  Name  Acceptance
        const parts = line.split(/\s{2,}/);
        if (parts.length >= 5) {
          results.push({
            id: parts[0],
            sitl: parts[1] || '-',
            expected: parts[2] || '-',
            verdict: parts[3] || '',
            name: parts[4] || '',
            acceptance: parts[5] || '',
          });
        }
      }
    }

    if (results.length === 0) return null;

    return { results, summary };
  }, []);

  // ログからシナリオ結果を抽出
  useEffect(() => {
    if (streamingLogs) {
      const parsed = parseScenarioResults(streamingLogs);
      if (parsed) {
        setScenarioResults(parsed);
      }
    }
  }, [streamingLogs, parseScenarioResults]);

  // テスト詳細のログからもシナリオ結果を抽出
  useEffect(() => {
    if (testDetail?.testLog) {
      const parsed = parseScenarioResults(testDetail.testLog);
      if (parsed) {
        setScenarioResults(parsed);
      }
    }
  }, [testDetail, parseScenarioResults]);

  // テストが終了している場合は inferPhase を使用（testStatusを考慮）、
  // 実行中の場合は apiPhases を優先
  const overallStatus = testStatus?.status || '';
  const isTestEnded = ['COMPLETED', 'FAILED', 'CANCELLED'].includes(overallStatus);

  const phaseStatuses = isTestEnded
    ? inferPhase(testStatus)  // 終了時はtestStatusから推測
    : apiPhases
      ? {
          fetch: apiPhases.fetch?.status || 'PENDING',
          build: apiPhases.build?.status || 'PENDING',
          test: apiPhases.test?.status || 'PENDING',
        }
      : inferPhase(testStatus);

  return (
    <div className="space-y-4">
      {/* ヘッダー */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* タイトル */}
        <h1 className="text-2xl font-bold text-slate-900">SiTLテスト</h1>

        {/* コントロールグループ */}
        <div className="flex items-center gap-4">
          {/* EC2グループ */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
              ec2Status?.state === 'running'
                ? 'bg-green-100 text-green-700'
                : ec2Status?.state === 'stopped'
                  ? 'bg-slate-200 text-slate-600'
                  : ec2Status?.state === 'pending' || ec2Status?.state === 'stopping'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-slate-200 text-slate-500'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                ec2Status?.state === 'running' ? 'bg-green-500 animate-pulse' :
                ec2Status?.state === 'stopped' ? 'bg-slate-400' :
                ec2Status?.state === 'pending' || ec2Status?.state === 'stopping' ? 'bg-yellow-500 animate-pulse' :
                'bg-slate-400'
              }`} />
              EC2 {ec2Status?.state === 'running' ? '稼働中' :
                   ec2Status?.state === 'stopped' ? '停止' :
                   ec2Status?.state === 'pending' ? '起動中' :
                   ec2Status?.state === 'stopping' ? '停止中' : '-'}
            </span>
            {ec2Status?.state === 'stopped' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleStartEC2}
                disabled={ec2ActionLoading}
              >
                {ec2ActionLoading ? (
                  <div className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Icon name="play" className="w-3 h-3" />
                )}
                起動
              </Button>
            )}
            {ec2Status?.state === 'running' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleStopEC2}
                disabled={ec2ActionLoading}
              >
                {ec2ActionLoading ? (
                  <div className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Icon name="square" className="w-3 h-3" />
                )}
                停止
              </Button>
            )}
            {/* 自動停止タイマー表示 */}
            {ec2Status?.state === 'running' && ec2Lifecycle?.stopTimerExpiresAt && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                ec2Lifecycle.maxExtensionsReached
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-blue-100 text-blue-700'
              }`}>
                <Icon name="clock" className="w-3 h-3" />
                {ec2Lifecycle.maxExtensionsReached
                  ? 'テスト終了後に停止'
                  : `${ec2Lifecycle.remainingMinutes}分後に自動停止`}
                {ec2Lifecycle.stopTimerExtensions > 0 && !ec2Lifecycle.maxExtensionsReached && (
                  <span className="text-xs opacity-75">(延長{ec2Lifecycle.stopTimerExtensions}/2)</span>
                )}
              </span>
            )}
            <button
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
              onClick={() => {
                const willShow = !showEc2Details;
                setShowEc2Details(willShow);
                if (willShow) {
                  if (!ec2Metrics) {
                    fetchEC2Metrics(ec2MetricsPeriod);
                  }
                  setTimeout(() => {
                    ec2DetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }
              }}
            >
              {showEc2Details ? '閉じる' : '詳細'}
            </button>
          </div>

          {/* テストグループ */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg">
            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={cleanDocker}
                onChange={(e) => setCleanDocker(e.target.checked)}
                className="rounded text-primary-500 w-3.5 h-3.5"
              />
              Dockerクリア
            </label>
            {runningTestId ? (
              <Button
                size="sm"
                variant="danger"
                onClick={handleStopTest}
                disabled={isStoppingTest}
              >
                {isStoppingTest ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    中止中...
                  </>
                ) : (
                  <>
                    <Icon name="square" className="w-3 h-3" />
                    テスト中止
                  </>
                )}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="primary"
                onClick={handleStartTestClick}
                disabled={isStartingTest}
              >
                {isStartingTest ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    開始中...
                  </>
                ) : (
                  <>
                    <Icon name="play" className="w-3 h-3" />
                    テスト実行
                  </>
                )}
              </Button>
            )}
          </div>

          {/* タイマーグループ */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg">
            {scheduledTests.length > 0 ? (
              <>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                <span className="text-xs text-blue-700 font-medium">
                  {scheduledTests.some(s => s.daily) ? '毎日 ' : ''}
                  {scheduledTests.filter(s => s.daily).map(s => s.dailyTime).join(', ')}
                  {scheduledTests.filter(s => s.daily).length > 0 && scheduledTests.filter(s => !s.daily).length > 0 ? ' + ' : ''}
                  {scheduledTests.filter(s => !s.daily).length > 0 ? `${scheduledTests.filter(s => !s.daily).length}件予約` : ''}
                </span>
              </>
            ) : (
              <span className="text-xs text-slate-400">スケジュールなし</span>
            )}
            <Button size="sm" variant="secondary" onClick={() => { setShowTimerModal(true); fetchBranches(); }}>
              <Icon name="clock" className="w-3 h-3" />
              タイマー
            </Button>
          </div>
        </div>
      </div>

      {/* エラー表示 */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <p className="text-red-700">{error}</p>
        </Card>
      )}

      {/* リアルタイム監視パネル */}
      {monitoringTestId && (
        <Card className={`border-2 ${
          testStatus?.status === 'COMPLETED' ? 'border-green-300 bg-green-50' :
          testStatus?.status === 'FAILED' ? 'border-red-300 bg-red-50' :
          testStatus?.status === 'CANCELLED' ? 'border-yellow-300 bg-yellow-50' :
          'border-blue-300 bg-blue-50'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {testStatus?.status === 'COMPLETED' ? (
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <Icon name="check" className="w-4 h-4 text-white" />
                </div>
              ) : testStatus?.status === 'FAILED' ? (
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                  <Icon name="x" className="w-4 h-4 text-white" />
                </div>
              ) : testStatus?.status === 'CANCELLED' ? (
                <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
                  <Icon name="alert-triangle" className="w-4 h-4 text-white" />
                </div>
              ) : (
                <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
              )}
              <div>
                <h3 className="font-bold text-slate-900">
                  {testStatus?.status === 'COMPLETED' ? 'テスト完了' :
                   testStatus?.status === 'FAILED' ? 'テスト失敗' :
                   testStatus?.status === 'CANCELLED' ? 'テスト中止' :
                   'リアルタイム監視'}
                </h3>
                <span className="text-sm text-slate-500">{monitoringTestId}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* 実行中のみ自動更新チェックボックス表示 */}
              {testStatus?.status === 'RUNNING' && (
                <label className="flex items-center gap-1 text-sm text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="rounded text-primary-500"
                  />
                  自動更新
                </label>
              )}
              <Button size="sm" variant="secondary" onClick={() => {
                fetchTestStatus(monitoringTestId);
                fetchLogStream(monitoringTestId);
              }}>
                <Icon name="refresh" className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="secondary" onClick={stopMonitoring}>
                <Icon name="x" className="w-3 h-3" /> 閉じる
              </Button>
            </div>
          </div>

          {/* フェーズ進捗 */}
          <div className="mb-4">
            <div className="flex items-center gap-2">
              {TEST_PHASES.map((phase, index) => {
                const status = phaseStatuses[phase.key];
                const isRunning = status === 'RUNNING';
                const isCompleted = status === 'COMPLETED';
                const isFailed = status === 'FAILED';

                return (
                  <React.Fragment key={phase.key}>
                    <div className="flex-1">
                      <div className={`flex items-center gap-2 p-3 rounded-lg border-2 ${
                        isRunning ? 'bg-blue-50 border-blue-400' :
                        isCompleted ? 'bg-green-50 border-green-400' :
                        isFailed ? 'bg-red-50 border-red-400' :
                        'bg-slate-50 border-slate-200'
                      }`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isRunning ? 'bg-blue-500' :
                          isCompleted ? 'bg-green-500' :
                          isFailed ? 'bg-red-500' :
                          'bg-slate-300'
                        }`}>
                          {isRunning ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : isCompleted ? (
                            <Icon name="check" className="w-4 h-4 text-white" />
                          ) : isFailed ? (
                            <Icon name="x" className="w-4 h-4 text-white" />
                          ) : (
                            <Icon name={phase.icon} className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{phase.label}</span>
                            {isRunning && (
                              <span className="text-xs text-blue-600 font-medium animate-pulse">実行中...</span>
                            )}
                            {isCompleted && (
                              <span className="text-xs text-green-600 font-medium">完了</span>
                            )}
                            {isFailed && (
                              <span className="text-xs text-red-600 font-medium">失敗</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">{phase.description}</div>
                        </div>
                      </div>
                    </div>
                    {index < TEST_PHASES.length - 1 && (
                      <Icon name="chevron-right" className={`w-5 h-5 ${
                        isCompleted ? 'text-green-500' : 'text-slate-300'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* コンテナステータス */}
          {testStatus?.containers && testStatus.containers.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-slate-700 mb-2">コンテナ状態</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {testStatus.containers.map((c, i) => (
                  <div key={i} className={`p-2 rounded text-xs ${c.healthy ? 'bg-green-100' : 'bg-slate-100'}`}>
                    <div className="font-medium truncate">
                      {c.name.replace('uav-dev-env-', '').replace('-1', '')}
                    </div>
                    <div className="text-slate-500 truncate">{c.status}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ストリーミングログ */}
          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-2">実行ログ</h4>
            <div
              ref={logRef}
              className="bg-slate-900 text-slate-100 rounded-lg p-3 font-mono text-xs overflow-auto h-64"
              style={{ overscrollBehavior: 'contain' }}
            >
              <pre className="whitespace-pre-wrap">{convertLogTimestamps(streamingLogs) || 'ログを取得中...'}</pre>
            </div>
          </div>
        </Card>
      )}

      {/* メインコンテンツ - 左右分割 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* 左: テスト実行履歴 */}
        <Card padding="p-0" className="lg:col-span-1">
          <div className="p-3 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-slate-900">テスト実行履歴</h3>
              {/* 監視中のテストに戻るボタン */}
              {monitoringTestId && selectedTest?.testId !== monitoringTestId && (
                <button
                  className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center gap-1"
                  onClick={() => {
                    const runningTest = testRuns.find((t) => t.testId === monitoringTestId);
                    if (runningTest) {
                      setSelectedTest(runningTest);
                    } else {
                      setSelectedTest({ testId: monitoringTestId, status: 'RUNNING' });
                    }
                  }}
                >
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  監視中に戻る
                </button>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1">{filteredTestRuns.length}件{hasMore && ` / ${totalCount}件中`}</p>
          </div>

          {/* フィルター */}
          <div className="p-3 border-b border-slate-200 bg-slate-50">
            <div className="flex flex-wrap gap-2">
              <input
                type="date"
                className="text-xs border border-slate-300 rounded px-2 py-1"
                placeholder="開始日"
                value={filter.dateFrom}
                onChange={(e) => setFilter({ ...filter, dateFrom: e.target.value })}
              />
              <input
                type="date"
                className="text-xs border border-slate-300 rounded px-2 py-1"
                placeholder="終了日"
                value={filter.dateTo}
                onChange={(e) => setFilter({ ...filter, dateTo: e.target.value })}
              />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setFilter({ status: '', dateFrom: '', dateTo: '' })}
              >
                クリア
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredTestRuns.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              テスト結果なし
            </div>
          ) : (
            <div className="max-h-[500px] overflow-y-auto divide-y divide-slate-100" style={{ overscrollBehavior: 'contain' }}>
              {filteredTestRuns.map((run) => (
                <div
                  key={run.testId}
                  className={`p-3 cursor-pointer hover:bg-slate-50 ${
                    selectedTest?.testId === run.testId ? 'bg-primary-50' : ''
                  }`}
                  onClick={() => setSelectedTest(run)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-sm text-slate-900 truncate">{formatTestIdToJST(run.testId)}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      run.status === 'COMPLETED' || run.hasVideo ? 'bg-green-100 text-green-700' :
                      run.status === 'RUNNING' ? 'bg-blue-100 text-blue-700' :
                      run.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {run.status || (run.hasVideo ? '完了' : '実行中')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <span>{run.createdAt ? formatDate(run.createdAt) : '-'}</span>
                      {run.cleanDocker && (
                        <span className="px-1 bg-yellow-100 text-yellow-700 rounded" title="Dockerクリーンアップ実行">
                          🧹
                        </span>
                      )}
                      {run.gitClean && (
                        <span className="px-1 bg-orange-100 text-orange-700 rounded" title="Gitクリーンビルド">
                          🔄
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {run.hasVideo && <span className="px-1 bg-blue-100 text-blue-700 rounded">動画</span>}
                      {run.hasScreenshots && <span className="px-1 bg-purple-100 text-purple-700 rounded">SS</span>}
                      <button
                        className={`px-1.5 py-0.5 rounded hover:opacity-80 ${
                          run.status === 'RUNNING'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          startMonitoring(run.testId);
                        }}
                      >
                        {run.status === 'RUNNING' ? 'リアルタイム監視' : 'テストログ'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {/* もっと見るボタン */}
              {hasMore && (
                <div className="p-3 text-center border-t border-slate-100">
                  <button
                    onClick={loadMoreTests}
                    disabled={loadingMore}
                    className="px-4 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg disabled:opacity-50"
                  >
                    {loadingMore ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                        読み込み中...
                      </span>
                    ) : (
                      `もっと見る（残り${totalCount - testRuns.length}件）`
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* 右パネル: FAILEDなら詳細、そうでなければCoverage */}
        <Card className="lg:col-span-2">
          {!selectedTest ? (
            <div className="text-center py-16 text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
              <Icon name="file" className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>左のリストからテストを選択してください</p>
            </div>
          ) : detailLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm text-slate-500">読み込み中...</p>
            </div>
          ) : !testDetail ? (
            <div className="text-center py-16 text-slate-400">
              <p>テスト詳細の取得に失敗しました</p>
            </div>
          ) : (selectedTest?.status === 'FAILED' || testDetail?.status === 'FAILED') ? (
            /* FAILEDの場合: 詳細を右パネルに表示 */
            <TestDetailContent
              testDetail={testDetail}
              screenshots={screenshots}
              coverageReport={coverageReport}
              formatDate={formatDate}
              calcDuration={calcDuration}
              handleWatchVideo={handleWatchVideo}
              handleViewScreenshot={handleViewScreenshot}
              handleDownloadLog={handleDownloadLog}
            />
          ) : scenarioResults && scenarioResults.results && scenarioResults.results.length > 0 ? (
            /* NOT FAILEDでCoverageがある場合: Coverageを右パネルに表示 */
            <ScenarioCoverageContent
              scenarioResults={scenarioResults}
              setScenarioResults={setScenarioResults}
            />
          ) : (
            /* NOT FAILEDでCoverageがない場合: 詳細を右パネルに表示 */
            <TestDetailContent
              testDetail={testDetail}
              screenshots={screenshots}
              coverageReport={coverageReport}
              formatDate={formatDate}
              calcDuration={calcDuration}
              handleWatchVideo={handleWatchVideo}
              handleViewScreenshot={handleViewScreenshot}
              handleDownloadLog={handleDownloadLog}
            />
          )}
        </Card>
      </div>

      {/* 下部: NOT FAILEDでCoverageがある場合のみ詳細を表示 */}
      {selectedTest && testDetail &&
       !(selectedTest?.status === 'FAILED' || testDetail?.status === 'FAILED') &&
       scenarioResults && scenarioResults.results && scenarioResults.results.length > 0 && (
        <Card className="mt-4">
          <TestDetailContent
            testDetail={testDetail}
            screenshots={screenshots}
            coverageReport={coverageReport}
            formatDate={formatDate}
            calcDuration={calcDuration}
            handleWatchVideo={handleWatchVideo}
            handleViewScreenshot={handleViewScreenshot}
            handleDownloadLog={handleDownloadLog}
          />
        </Card>
      )}

      {/* EC2詳細パネル（常駐・折りたたみ式） */}
      <Card ref={ec2DetailsRef} className="mt-4">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => {
            const willShow = !showEc2Details;
            setShowEc2Details(willShow);
            if (willShow && !ec2Metrics) {
              fetchEC2Metrics(ec2MetricsPeriod);
            }
          }}
        >
          <div className="flex items-center gap-3">
            <Icon name={showEc2Details ? 'chevron-down' : 'chevron-right'} className="w-4 h-4 text-slate-400" />
            <h3 className="font-medium text-slate-900">EC2詳細情報</h3>
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              ec2Status?.state === 'running' ? 'bg-green-100 text-green-700' :
              ec2Status?.state === 'stopped' ? 'bg-red-100 text-red-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>
              {ec2Status?.state === 'running' ? '稼働中' :
               ec2Status?.state === 'stopped' ? '停止' : '処理中'}
            </span>
            {!showEc2Details && ec2Metrics && (
              <span className="text-xs text-slate-500">
                今月: {ec2Metrics.monthlyRunningHours || 0}時間 / ${ec2Metrics.monthlyCost || 0}
              </span>
            )}
          </div>
          {showEc2Details && (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <select
                value={ec2MetricsPeriod}
                onChange={(e) => {
                  setEc2MetricsPeriod(e.target.value);
                  fetchEC2Metrics(e.target.value);
                }}
                className="text-sm border border-slate-300 rounded px-2 py-1"
              >
                <option value="1h">過去1時間</option>
                <option value="6h">過去6時間</option>
                <option value="24h">過去24時間</option>
                <option value="7d">過去7日間</option>
                <option value="30d">過去30日間</option>
              </select>
              <Button size="sm" variant="secondary" onClick={() => fetchEC2Metrics(ec2MetricsPeriod)}>
                <Icon name="refresh" className="w-3 h-3" />
              </Button>
            </div>
          )}
        </div>

        {showEc2Details && (
          <div className="mt-4">

          {ec2MetricsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : ec2Metrics ? (
            <div className="space-y-4">
              {/* 基本情報 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <div className="text-xs text-slate-500 mb-1">インスタンスタイプ</div>
                  <div className="font-medium text-slate-900">{ec2Status?.instanceType || '-'}</div>
                  <div className="text-xs text-slate-500">{ec2Status?.specs?.description || ''}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">スペック</div>
                  <div className="font-medium text-slate-900">
                    {ec2Status?.specs?.vCPUs || '-'} vCPU / {ec2Status?.specs?.memory || '-'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">今月の稼働時間</div>
                  <div className="font-medium text-slate-900">{ec2Metrics.monthlyRunningHours || 0} 時間</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">今月のコスト概算</div>
                  <div className="font-medium text-slate-900">${ec2Metrics.monthlyCost || 0} USD</div>
                  <div className="text-xs text-slate-500">({Math.round((ec2Metrics.monthlyCost || 0) * 150)}円)</div>
                </div>
              </div>

              {/* キャパシティ情報 */}
              {ec2Metrics.capacity && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 mb-3 font-medium">リソース上限</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500">ネットワーク帯域</div>
                      <div className="font-medium text-slate-900">
                        最大 {ec2Metrics.capacity.networkBandwidthGbps || 12.5} Gbps
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-500">EBS帯域</div>
                      <div className="font-medium text-slate-900">
                        最大 {ec2Metrics.capacity.ebsBandwidthGbps || 10} Gbps
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-500">EBSボリューム</div>
                      <div className="font-medium text-slate-900">
                        {ec2Metrics.capacity.ebsVolumeType || 'gp3'} / {ec2Metrics.capacity.ebsSizeGiB || '-'} GiB
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-500">EBS IOPS / スループット</div>
                      <div className="font-medium text-slate-900">
                        {ec2Metrics.capacity.ebsIops || 3000} IOPS / {ec2Metrics.capacity.ebsThroughputMBps || 125} MB/s
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ストレージ使用状況 */}
              {ec2Status?.storage && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="text-xs text-slate-500 mb-2">ストレージ使用状況</div>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            parseInt(ec2Status.storage.percentUsed) > 80 ? 'bg-red-500' :
                            parseInt(ec2Status.storage.percentUsed) > 60 ? 'bg-yellow-500' : 'bg-blue-500'
                          }`}
                          style={{ width: ec2Status.storage.percentUsed }}
                        />
                      </div>
                    </div>
                    <div className="text-sm font-medium text-slate-700">
                      {ec2Status.storage.used} / {ec2Status.storage.total} ({ec2Status.storage.percentUsed})
                    </div>
                  </div>
                </div>
              )}

              {/* メトリクスグラフ */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* CPU使用率 */}
                <div className="p-4 bg-white border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-slate-700">
                      {ec2Metrics.metrics?.CPUUtilization?.label || 'CPU使用率'}
                    </span>
                    <span className="text-xs text-slate-400">
                      ({ec2Metrics.metrics?.CPUUtilization?.stat || 'Maximum'})
                    </span>
                  </div>
                  <MetricChart
                    datapoints={ec2Metrics.metrics?.CPUUtilization?.datapoints || []}
                    unit="%"
                    color="#3b82f6"
                    period={ec2MetricsPeriod}
                    tooltip={`【CPU使用率 - Maximum統計】
各サンプリング期間内の最大CPU使用率を表示。

■ サンプリング間隔:
・1時間: 1分間隔
・6時間: 5分間隔
・24時間: 15分間隔
・7日/30日: 1時間間隔

■ 計算方法:
各間隔内でCPUが最も高かった瞬間の値。
100%に近いほどCPUリソースが逼迫。
80%以上が継続する場合はインスタンス
タイプのアップグレードを検討。`}
                  />
                </div>

                {/* メモリ使用率 */}
                {ec2Metrics.metrics?.mem_used_percent && (
                  <div className="p-4 bg-white border border-slate-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-slate-700">
                        {ec2Metrics.metrics?.mem_used_percent?.label || 'メモリ使用率'}
                      </span>
                      <span className="text-xs text-slate-400">
                        ({ec2Metrics.metrics?.mem_used_percent?.stat || 'Average'})
                      </span>
                      {ec2Metrics.capacity?.memoryGiB && (
                        <span className="text-xs text-blue-600 ml-auto">
                          搭載: {ec2Metrics.capacity.memoryGiB} GiB
                        </span>
                      )}
                    </div>
                    <MetricChart
                      datapoints={ec2Metrics.metrics?.mem_used_percent?.datapoints || []}
                      unit="%"
                      color="#8b5cf6"
                      period={ec2MetricsPeriod}
                      tooltip={`【メモリ使用率 - Average統計】
各サンプリング期間内の平均メモリ使用率。

■ 搭載メモリ:
${ec2Metrics.capacity?.memoryGiB || 16} GiB

■ 目安:
・80%以上が継続 → メモリ不足の可能性
・Dockerコンテナ、シミュレータで消費
・OOMキラー発動前に対処が必要

※ CloudWatch Agent経由で取得`}
                    />
                  </div>
                )}

                {/* ディスク使用率 */}
                {ec2Metrics.metrics?.disk_used_percent && (
                  <div className="p-4 bg-white border border-slate-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-slate-700">
                        {ec2Metrics.metrics?.disk_used_percent?.label || 'ディスク使用率'}
                      </span>
                      <span className="text-xs text-slate-400">
                        ({ec2Metrics.metrics?.disk_used_percent?.stat || 'Average'})
                      </span>
                      {ec2Metrics.capacity?.ebsSizeGiB && (
                        <span className="text-xs text-blue-600 ml-auto">
                          容量: {ec2Metrics.capacity.ebsSizeGiB} GiB
                        </span>
                      )}
                    </div>
                    <MetricChart
                      datapoints={ec2Metrics.metrics?.disk_used_percent?.datapoints || []}
                      unit="%"
                      color="#f97316"
                      period={ec2MetricsPeriod}
                      tooltip={`【ディスク使用率 - Average統計】
ルートボリューム（/）の使用率。

■ ボリューム情報:
タイプ: ${ec2Metrics.capacity?.ebsVolumeType || 'gp3'}
容量: ${ec2Metrics.capacity?.ebsSizeGiB || '-'} GiB

■ 注意:
・Dockerイメージ、ログで増加
・80%超えで警告、90%超えで危険
・docker system pruneで不要イメージ削除

※ CloudWatch Agent経由で取得`}
                    />
                  </div>
                )}

                {/* ネットワーク受信 */}
                <div className="p-4 bg-white border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-slate-700">ネットワーク受信</span>
                    <span className="text-xs text-slate-400">(期間内合計)</span>
                    <span className="text-xs text-blue-600 ml-auto">
                      上限: {ec2Metrics.capacity?.networkBandwidthGbps || 12.5} Gbps
                    </span>
                  </div>
                  <MetricChart
                    datapoints={ec2Metrics.metrics?.NetworkIn?.datapoints || []}
                    unit=""
                    color="#10b981"
                    formatValue={(v) => formatBytes(v)}
                    period={ec2MetricsPeriod}
                    tooltip={`【ネットワーク受信 - Sum統計】
各サンプリング期間内の受信バイト数合計。

■ 上限との比較:
c8i.2xlargeの帯域上限は12.5Gbps
= 約1.56GB/秒

■ 目安:
・1分間隔で100MB以上が継続
  → 帯域の10%程度を使用
・1GB/分以上が継続する場合は
  ネットワーク最適化を検討`}
                  />
                </div>

                {/* ネットワーク送信 */}
                <div className="p-4 bg-white border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-slate-700">ネットワーク送信</span>
                    <span className="text-xs text-slate-400">(期間内合計)</span>
                    <span className="text-xs text-blue-600 ml-auto">
                      上限: {ec2Metrics.capacity?.networkBandwidthGbps || 12.5} Gbps
                    </span>
                  </div>
                  <MetricChart
                    datapoints={ec2Metrics.metrics?.NetworkOut?.datapoints || []}
                    unit=""
                    color="#f59e0b"
                    formatValue={(v) => formatBytes(v)}
                    period={ec2MetricsPeriod}
                    tooltip={`【ネットワーク送信 - Sum統計】
各サンプリング期間内の送信バイト数合計。

■ 上限との比較:
c8i.2xlargeの帯域上限は12.5Gbps
= 約1.56GB/秒

■ 注意:
SiTLテストでは動画ストリーミング等で
送信量が増加することがあります。`}
                  />
                </div>

                {/* ディスクスループット（読込） */}
                <div className="p-4 bg-white border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-slate-700">ディスク読込</span>
                    <span className="text-xs text-slate-400">(期間内合計)</span>
                    <span className="text-xs text-blue-600 ml-auto">
                      上限: {ec2Metrics.capacity?.ebsThroughputMBps || 125} MB/s
                    </span>
                  </div>
                  <MetricChart
                    datapoints={ec2Metrics.metrics?.VolumeReadBytes?.datapoints || []}
                    unit=""
                    color="#8b5cf6"
                    formatValue={(v) => formatBytes(v)}
                    period={ec2MetricsPeriod}
                    tooltip={`【ディスク読込 - Sum統計】
各サンプリング期間内の読込バイト数合計。

■ gp3ボリュームの上限:
ベースライン: 125 MB/s
（プロビジョニングで最大1,000MB/sまで増加可能）

■ 目安:
Dockerイメージ読込時に増加。
継続的に100MB/s以上の場合は
スループット増加を検討。`}
                  />
                </div>

                {/* ディスクスループット（書込） */}
                <div className="p-4 bg-white border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-slate-700">ディスク書込</span>
                    <span className="text-xs text-slate-400">(期間内合計)</span>
                    <span className="text-xs text-blue-600 ml-auto">
                      上限: {ec2Metrics.capacity?.ebsThroughputMBps || 125} MB/s
                    </span>
                  </div>
                  <MetricChart
                    datapoints={ec2Metrics.metrics?.VolumeWriteBytes?.datapoints || []}
                    unit=""
                    color="#ec4899"
                    formatValue={(v) => formatBytes(v)}
                    period={ec2MetricsPeriod}
                    tooltip={`【ディスク書込 - Sum統計】
各サンプリング期間内の書込バイト数合計。

■ gp3ボリュームの上限:
ベースライン: 125 MB/s

■ 注意:
ビルド時のDockerレイヤー書込、
ログ出力等で増加します。`}
                  />
                </div>

                {/* IOPS（読込） */}
                {ec2Metrics.metrics?.VolumeReadOps && (
                  <div className="p-4 bg-white border border-slate-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-slate-700">ディスクIOPS（読込）</span>
                      <span className="text-xs text-slate-400">(期間内合計)</span>
                      <span className="text-xs text-blue-600 ml-auto">
                        上限: {ec2Metrics.capacity?.ebsIops || 3000} IOPS
                      </span>
                    </div>
                    <MetricChart
                      datapoints={ec2Metrics.metrics?.VolumeReadOps?.datapoints || []}
                      unit="ops"
                      color="#14b8a6"
                      period={ec2MetricsPeriod}
                      tooltip={`【ディスクIOPS（読込） - Sum統計】
各サンプリング期間内の読込操作回数。

■ gp3ボリュームの上限:
ベースライン: 3,000 IOPS
（プロビジョニングで最大16,000 IOPSまで増加可能）

■ 計算方法:
1分間隔なら「その1分間の総読込回数」
60で割ると秒間IOPS概算値`}
                    />
                  </div>
                )}

                {/* IOPS（書込） */}
                {ec2Metrics.metrics?.VolumeWriteOps && (
                  <div className="p-4 bg-white border border-slate-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-slate-700">ディスクIOPS（書込）</span>
                      <span className="text-xs text-slate-400">(期間内合計)</span>
                      <span className="text-xs text-blue-600 ml-auto">
                        上限: {ec2Metrics.capacity?.ebsIops || 3000} IOPS
                      </span>
                    </div>
                    <MetricChart
                      datapoints={ec2Metrics.metrics?.VolumeWriteOps?.datapoints || []}
                      unit="ops"
                      color="#f97316"
                      period={ec2MetricsPeriod}
                      tooltip={`【ディスクIOPS（書込） - Sum統計】
各サンプリング期間内の書込操作回数。

■ gp3ボリュームの上限:
ベースライン: 3,000 IOPS

■ 注意:
読込+書込の合計で3,000 IOPSが上限。
継続的に上限に達する場合は
IOPSプロビジョニングを検討。`}
                    />
                  </div>
                )}

                {/* バーストバランス（gp2のみ） */}
                {ec2Metrics.metrics?.BurstBalance ? (
                  <div className="p-4 bg-white border border-slate-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-slate-700">EBSバーストバランス</span>
                      <span className="text-xs text-slate-400">(Average)</span>
                    </div>
                    <MetricChart
                      datapoints={ec2Metrics.metrics?.BurstBalance?.datapoints || []}
                      unit="%"
                      color="#06b6d4"
                      period={ec2MetricsPeriod}
                      tooltip={`【EBSバーストバランス - Average統計】
gp2ボリュームのバースト残量（%）。

■ 注意:
0%になるとIOPS性能が低下。
20%以下が継続する場合はgp3への
移行を検討してください。`}
                    />
                  </div>
                ) : ec2Metrics.capacity?.ebsVolumeType === 'gp3' && (
                  <div className="p-4 bg-white border border-slate-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-slate-700">EBSバーストバランス</span>
                    </div>
                    <div className="flex items-center justify-center h-32 text-slate-500 text-sm">
                      <div className="text-center">
                        <div className="text-green-600 font-medium mb-1">✓ gp3ボリューム</div>
                        <div className="text-xs text-slate-400">
                          gp3は常に一定のIOPS/スループットを提供<br />
                          バーストバランスは不要です
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center text-slate-500 py-8">
              メトリクスを取得できませんでした
            </div>
          )}
          </div>
        )}
      </Card>

      {/* 動画再生モーダル */}
      <Modal
        isOpen={showVideoModal}
        onClose={() => { setShowVideoModal(false); setVideoUrl(null); }}
        title="テスト動画"
        size="xl"
        footer={
          <Button variant="secondary" onClick={() => { setShowVideoModal(false); setVideoUrl(null); }}>
            閉じる
          </Button>
        }
      >
        {videoUrl ? (
          <div className="aspect-video bg-black rounded-lg overflow-hidden">
            <video src={videoUrl} controls autoPlay className="w-full h-full" />
          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </Modal>

      {/* スクリーンショット表示モーダル */}
      <Modal
        isOpen={showScreenshotModal}
        onClose={() => { setShowScreenshotModal(false); setScreenshotUrl(null); }}
        title={selectedScreenshot || 'スクリーンショット'}
        size="xl"
        footer={
          <Button variant="secondary" onClick={() => { setShowScreenshotModal(false); setScreenshotUrl(null); }}>
            閉じる
          </Button>
        }
      >
        {screenshotUrl ? (
          <div className="flex items-center justify-center bg-slate-100 rounded-lg overflow-hidden">
            <img src={screenshotUrl} alt={selectedScreenshot} className="max-w-full max-h-[70vh] object-contain" />
          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </Modal>

      {/* テスト実行確認モーダル */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="テスト実行の確認"
        size="sm"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
              キャンセル
            </Button>
            <Button variant="primary" onClick={handleStartTest}>
              <Icon name="play" className="w-4 h-4" />
              実行開始
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-slate-700">SiTLテストを実行しますか？</p>

          {/* ブランチ設定 */}
          <div className="bg-slate-50 rounded-lg p-3">
            <label className="block text-sm font-medium text-slate-700 mb-1">ブランチ</label>
            <div className="flex gap-2">
              <select
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm"
              >
                {branches.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={fetchBranches}
                disabled={branchesLoading}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-100 disabled:opacity-50"
                title="ブランチ一覧を更新"
              >
                {branchesLoading ? (
                  <span className="inline-block w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Icon name="refresh" className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* オプション */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={cleanDocker}
                onChange={(e) => setCleanDocker(e.target.checked)}
                className="rounded text-primary-500"
              />
              Dockerクリーンアップ
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={gitClean}
                onChange={(e) => setGitClean(e.target.checked)}
                className="rounded text-primary-500"
              />
              Gitクリーンビルド (git clone)
            </label>
            <p className="text-xs text-slate-500 ml-6">
              チェックなし = git fetch/pull（増分更新）
            </p>
          </div>

          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
            <div className="font-medium mb-2">実行される工程:</div>
            <ol className="list-decimal list-inside space-y-1">
              <li>モジュール取得 ({gitClean ? 'Git clone' : 'Git fetch'}, Docker pull)</li>
              <li>ビルド (Docker image build)</li>
              <li>テスト実行 (E2E / Maestro tests)</li>
            </ol>
          </div>
          {(cleanDocker || gitClean) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
              <div className="flex items-center gap-2">
                <Icon name="alert-triangle" className="w-4 h-4" />
                <span className="font-medium">クリーンビルド有効</span>
              </div>
              <div className="mt-1 text-xs space-y-1">
                {cleanDocker && <p>・Dockerコンテナとイメージを削除</p>}
                {gitClean && <p>・リポジトリを削除して新規clone</p>}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* タイマー設定モーダル */}
      <Modal
        isOpen={showTimerModal}
        onClose={() => setShowTimerModal(false)}
        title="スケジュール管理"
        size="xl"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowTimerModal(false)}>
              閉じる
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 左側: 新規スケジュール設定 */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2 pb-2 border-b border-slate-200">
              <Icon name="plus" className="w-4 h-4" />
              新規スケジュール
            </h4>

            {/* Daily/一回限りの切り替え */}
            <div className="flex gap-4 p-3 bg-slate-50 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scheduleType"
                  checked={!dailyMode}
                  onChange={() => setDailyMode(false)}
                  className="text-primary-500"
                />
                <span className="text-sm font-medium text-slate-700">一回限り</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scheduleType"
                  checked={dailyMode}
                  onChange={() => setDailyMode(true)}
                  className="text-primary-500"
                />
                <span className="text-sm font-medium text-slate-700">毎日実行</span>
              </label>
            </div>

            {/* 一回限りの場合: 日時指定 */}
            {!dailyMode && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  実行日時
                </label>
                <input
                  type="datetime-local"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
            )}

            {/* Dailyの場合: 時刻のみ */}
            {dailyMode && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  実行時刻 (毎日)
                </label>
                <input
                  type="time"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={dailyTime}
                  onChange={(e) => setDailyTime(e.target.value)}
                />
              </div>
            )}

            {/* ブランチ設定 */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">ブランチ</label>
              <div className="flex gap-2">
                <select
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm"
                >
                  {branches.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={fetchBranches}
                  disabled={branchesLoading}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-100 disabled:opacity-50"
                  title="更新"
                >
                  {branchesLoading ? (
                    <span className="inline-block w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icon name="refresh" className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* オプション */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cleanDocker}
                  onChange={(e) => setCleanDocker(e.target.checked)}
                  className="rounded text-primary-500"
                />
                Dockerクリーンアップ
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={gitClean}
                  onChange={(e) => setGitClean(e.target.checked)}
                  className="rounded text-primary-500"
                />
                Gitクリーンビルド
              </label>
            </div>

            {/* 登録ボタン */}
            <Button
              variant="primary"
              onClick={handleScheduleTest}
              disabled={dailyMode ? !dailyTime : !scheduledTime}
              className="w-full"
            >
              <Icon name="clock" className="w-4 h-4" />
              スケジュール登録
            </Button>

            <p className="text-xs text-slate-500 bg-blue-50 p-2 rounded">
              EC2停止中の場合は自動起動し、テスト完了後に停止します。
            </p>
          </div>

          {/* 右側: スケジュール一覧 */}
          <div className="space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-slate-200">
              <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <Icon name="calendar" className="w-4 h-4" />
                スケジュール一覧 ({scheduledTests.length}件)
              </h4>
              {scheduledTests.length > 0 && (
                <button
                  className="text-xs text-red-600 hover:text-red-800 underline"
                  onClick={async () => {
                    if (!window.confirm('すべてのスケジュールをクリアしますか？')) return;
                    for (const schedule of scheduledTests) {
                      try {
                        await buildApi.cancelScheduledTest(schedule.scheduleId);
                      } catch (err) {
                        console.error('Failed to cancel:', err);
                      }
                    }
                    fetchScheduledTests();
                  }}
                >
                  すべてクリア
                </button>
              )}
            </div>

            {scheduledTests.length === 0 ? (
              <div className="text-center py-8 text-slate-400 bg-slate-50 rounded-lg">
                <Icon name="calendar" className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">スケジュールなし</p>
                <p className="text-xs mt-1">左のフォームから登録してください</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {/* 毎日スケジュール */}
                {scheduledTests.filter(s => s.daily).length > 0 && (
                  <div className="mb-3">
                    <div className="text-xs font-medium text-blue-600 mb-1 flex items-center gap-1">
                      <Icon name="repeat" className="w-3 h-3" />
                      毎日実行
                    </div>
                    {scheduledTests.filter(s => s.daily).map((schedule) => (
                      <div
                        key={schedule.scheduleId}
                        className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-200 mb-1"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900 text-sm">
                            {schedule.dailyTime}
                          </span>
                          <span className="text-xs text-slate-500">
                            {schedule.branch || 'main'}
                          </span>
                          {schedule.cleanDocker && (
                            <span className="px-1 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">
                              Docker
                            </span>
                          )}
                          {schedule.gitClean && (
                            <span className="px-1 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">
                              Git
                            </span>
                          )}
                        </div>
                        <button
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
                          onClick={() => handleCancelSchedule(schedule.scheduleId)}
                          title="削除"
                        >
                          <Icon name="trash" className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* 一回限りスケジュール */}
                {scheduledTests.filter(s => !s.daily).length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-slate-600 mb-1 flex items-center gap-1">
                      <Icon name="clock" className="w-3 h-3" />
                      予約実行
                    </div>
                    {scheduledTests.filter(s => !s.daily).map((schedule) => (
                      <div
                        key={schedule.scheduleId}
                        className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-200 mb-1"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900 text-sm">
                            {new Date(schedule.scheduledTime).toLocaleString('ja-JP', {
                              month: 'numeric', day: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                          <span className="text-xs text-slate-500">
                            {schedule.branch || 'main'}
                          </span>
                          {schedule.cleanDocker && (
                            <span className="px-1 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">
                              Docker
                            </span>
                          )}
                          {schedule.gitClean && (
                            <span className="px-1 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">
                              Git
                            </span>
                          )}
                        </div>
                        <button
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
                          onClick={() => handleCancelSchedule(schedule.scheduleId)}
                          title="削除"
                        >
                          <Icon name="trash" className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>

    </div>
  );
}
